import { useEffect, useState } from 'react';
import { makeStyles, tokens, Button, Spinner, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { LockClosed20Regular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../../authConfig';
import { LOGO_URL, PRODUCT_NAME } from '../../branding';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f5f7fa',
  },
  heroSection: {
    flex: '1 1 55%',
    background: 'linear-gradient(135deg, #0b5789 0%, #0f4c75 55%, #1d7f8c 100%)',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '64px',
    '@media (max-width: 900px)': {
      display: 'none',
    },
  },
  heroContent: {
    maxWidth: '560px',
  },
  eyebrow: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#8fd8d9',
    marginBottom: '16px',
  },
  headline: {
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: 1.15,
    margin: '0 0 24px',
  },
  description: {
    fontSize: '17px',
    lineHeight: 1.6,
    opacity: 0.9,
    margin: 0,
  },
  loginSection: {
    flex: '1 1 45%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    backgroundColor: 'white',
  },
  loginCard: {
    width: '100%',
    maxWidth: '380px',
  },
  logo: {
    width: '220px',
    height: 'auto',
    display: 'block',
    marginBottom: '40px',
  },
  loginTitle: {
    fontSize: '26px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    margin: '0 0 8px',
  },
  loginSubtitle: {
    fontSize: '15px',
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground3,
    margin: '0 0 32px',
  },
  signInButton: {
    width: '100%',
    height: '48px',
    fontSize: '15px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  microsoftIcon: {
    width: '20px',
    height: '20px',
    marginRight: '8px',
  },
  ssoNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  error: {
    marginBottom: '16px',
  },
});

export function LoginPage() {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Already signed in: go straight to the wizard.
  useEffect(() => {
    if (accounts.length > 0) {
      navigate('/deploy', { replace: true });
    }
  }, [accounts, navigate]);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Sign-in could not be started.');
      setIsLoggingIn(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>Deployment Manager</span>
          <h1 className={styles.headline}>
            Deploy FirstIQ products into customer Microsoft&nbsp;365 tenants
          </h1>
          <p className={styles.description}>
            Connect to a customer tenant, confirm the product is licensed in
            FirstIQ Guardian, then install the product package and its launch
            page on the customer&apos;s SharePoint site.
          </p>
        </div>
      </div>

      <div className={styles.loginSection}>
        <div className={styles.loginCard}>
          <img className={styles.logo} src={LOGO_URL} alt="FirstIQ" />
          <h2 className={styles.loginTitle}>Sign in</h2>
          <p className={styles.loginSubtitle}>
            {PRODUCT_NAME} is for First Digital deployment engineers. Sign in
            with your organisation account to continue.
          </p>

          {loginError && (
            <MessageBar intent="error" className={styles.error}>
              <MessageBarBody>{loginError}</MessageBarBody>
            </MessageBar>
          )}

          <Button
            appearance="primary"
            size="large"
            className={styles.signInButton}
            onClick={handleLogin}
            disabled={isLoggingIn}
          >
            {isLoggingIn ? (
              <Spinner size="tiny" />
            ) : (
              <>
                <svg
                  className={styles.microsoftIcon}
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect width="10" height="10" fill="#f25022" />
                  <rect x="11" width="10" height="10" fill="#7fba00" />
                  <rect y="11" width="10" height="10" fill="#00a4ef" />
                  <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
                </svg>
                Sign in with Microsoft
              </>
            )}
          </Button>

          <div className={styles.ssoNote}>
            <LockClosed20Regular />
            <span>Microsoft Entra ID single sign-on</span>
          </div>
        </div>
      </div>
    </div>
  );
}
